import { useEffect, useMemo, useRef, useState } from 'react';
import { Toggle } from '@/components/ui/Toggle';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  useFeatureRegistry,
  useQuotaRegistry,
  usePackageEntitlements,
  useSetPackageEntitlement,
  usePackageQuotas,
  useSetPackageQuota,
} from '@/hooks/useAdmin';
import { ICONS } from '@/constants/icons';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';
import { parseIntegerInput } from '@/utils/parseIntegerInput';
import type { EntitlementSource, PackageEntitlementToggle, PackageQuotaRow } from '@/types/admin';

/**
 * Per-package feature toggles (super-admin). Each toggle saves immediately
 * (optimistic, rolled back on error). This is the single place to control the
 * gated features, including the package-column ones (Canva, Brand Check,
 * Google Drive, Analytics, Atelier Mode).
 */

const SOURCE_BADGE: Record<EntitlementSource, { label: string; className: string; title: string }> = {
  row: { label: 'feature row', className: 'bg-gray-100 text-gray-600', title: 'Stored as a package feature row' },
  column: { label: 'package field', className: 'bg-peach text-secondary-700', title: 'Stored on the package itself' },
  addon: {
    label: 'add-on',
    className: 'bg-soft-mint text-secondary-700',
    title: 'Package field here; an add-on can also grant this to a team',
  },
};

/** Create-mode draft: nothing is saved until the package is created; the modal owns this state. */
export interface PackageDraft {
  /** Explicitly toggled row keys (an absent key = ON, the backend default) */
  toggles: Record<string, boolean>;
  onToggle: (key: string, enabled: boolean) => void;
  /** Monthly limits the admin set (absent = unlimited); featureKey lets the modal drop hidden ones */
  quotas: Record<string, { monthlyLimit: number | null; featureKey: string | null }>;
  onQuotaChange: (quota: PackageQuotaRow, monthlyLimit: number | null) => void;
}

interface Props {
  /** null = create mode (draft) */
  packageId: number | null;
  /** Package.isAgency (live form value): portfolio / release features only exist in Brand Portfolios */
  isAgency: boolean;
  /** Saved value of Package.maxBrandProfiles (null = system maximum), edited under the Brand World switch */
  maxBrandProfiles: number | null;
  /** Edit mode: saves { maxBrandProfiles } at once. Draft mode: stores it in the form. Rejects on failure. */
  onCommitMaxBrandProfiles: (value: number | null) => Promise<void> | void;
  /** Create/edit gate: true while the Brand World quantity holds an invalid entry */
  onMaxBrandProfilesInvalidChange?: (invalid: boolean) => void;
  draft?: PackageDraft;
}

/** Registry groups (from the API's `group` field) that only exist for Brand Portfolio packages */
export const PORTFOLIO_ONLY_GROUPS: ReadonlySet<string> = new Set(['portfolio', 'release']);

const MAX_MONTHLY_LIMIT = 1_000_000;

interface QuotaInputProps {
  quota: PackageQuotaRow;
  disabled: boolean;
  /** Bumped after a failed save so the draft snaps back to the saved value */
  resetKey: number;
  onCommit: (quota: PackageQuotaRow, monthlyLimit: number | null) => void;
}

/**
 * "per month" limit input. Empty = Unlimited. Saves on blur / Enter when the value changed
 * (integer 0..1,000,000); an invalid value is reverted with an inline message.
 */
const QuotaInput = ({ quota, disabled, resetKey, onCommit }: QuotaInputProps) => {
  const saved = quota.monthlyLimit === null ? '' : String(quota.monthlyLimit);
  const [draft, setDraft] = useState(saved);
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(saved);
    setInvalid(false);
  }, [saved, resetKey]);

  const commit = () => {
    // A non-numeric entry reads as '' but is NOT "Unlimited": reject it instead of saving null
    if (inputRef.current?.validity.badInput) {
      setInvalid(true);
      setDraft(saved);
      if (inputRef.current) inputRef.current.value = saved;
      return;
    }
    const trimmed = draft.trim();
    if (trimmed === saved) {
      setInvalid(false);
      return;
    }
    if (trimmed === '') {
      setInvalid(false);
      onCommit(quota, null);
      return;
    }
    const value = Number(trimmed);
    if (!Number.isInteger(value) || value < 0 || value > MAX_MONTHLY_LIMIT) {
      setInvalid(true);
      setDraft(saved);
      return;
    }
    setInvalid(false);
    onCommit(quota, value);
  };

  const inputId = `quota-${quota.quotaKey}`;
  return (
    <div className="flex flex-col items-start sm:items-end gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={inputId} className="sr-only">
          {`${quota.label}: ${quota.unitLabel} per month (empty = Unlimited)`}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_MONTHLY_LIMIT}
          step={1}
          value={draft}
          disabled={disabled}
          placeholder="Unlimited"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          aria-invalid={invalid}
          className={`w-28 rounded-lg border bg-white px-2 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none ${
            invalid ? 'border-red-500' : 'border-gray-300 focus:border-primary-500'
          }`}
        />
        <span className="text-xs text-gray-500">{`${quota.unitLabel} per month`}</span>
      </div>
      {invalid && (
        <span role="alert" className="text-xs text-red-600">
          Whole number from 0 to 1,000,000, or empty for Unlimited.
        </span>
      )}
    </div>
  );
};

const MAX_BRAND_PROFILES = 10;

interface BrandProfilesInputProps {
  saved: number | null;
  onCommit: (value: number | null) => Promise<void> | void;
  /** Reports whether the field currently holds an invalid entry (the modal blocks submit while true) */
  onInvalidChange?: (invalid: boolean) => void;
}

/**
 * "Profiles per brand" input under the Brand World switch. Commits on blur / Enter like the monthly limits
 * (integer 0..10, empty = system maximum); invalid input keeps the typed text and shows an error, nothing is saved.
 */
const BrandProfilesInput = ({ saved, onCommit, onInvalidChange }: BrandProfilesInputProps) => {
  const savedText = saved === null ? '' : String(saved);
  const [draft, setDraft] = useState(savedText);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onInvalidRef = useRef(onInvalidChange);
  onInvalidRef.current = onInvalidChange;
  const fail = (message: string | null) => {
    setError(message);
    onInvalidRef.current?.(message !== null);
  };

  useEffect(() => {
    setDraft(savedText);
    fail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedText]);

  // Never leave a stale "invalid" flag behind when the input goes away
  useEffect(() => () => onInvalidRef.current?.(false), []);

  const commit = async () => {
    if (inFlight.current) return;
    const input = inputRef.current;
    const raw = input?.value ?? draft;
    const parsed = parseIntegerInput(raw);
    if (
      input?.validity.badInput ||
      input?.validity.stepMismatch ||
      parsed.kind === 'invalid' ||
      (parsed.kind === 'ok' && parsed.value > MAX_BRAND_PROFILES)
    ) {
      fail(`Whole number from 0 to ${MAX_BRAND_PROFILES}, or empty for the system maximum.`);
      return;
    }
    const value = parsed.kind === 'ok' ? parsed.value : null;
    if (value === saved) {
      fail(null);
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      await onCommit(value);
      fail(null);
    } catch (err: unknown) {
      // Snap back to the saved value, like the monthly limits do
      setDraft(savedText);
      const message = formatApiError(err).message || 'Could not save';
      setError(message);
      onInvalidRef.current?.(false);
      toast.error(message);
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-start sm:items-end gap-1" data-testid="brand-world-profiles-field">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="brand-world-max-profiles" className="sr-only">
          Brand World profiles per brand
        </label>
        <input
          id="brand-world-max-profiles"
          data-testid="brand-world-profiles-input"
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_BRAND_PROFILES}
          step={1}
          value={draft}
          disabled={saving}
          placeholder="10"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          aria-invalid={!!error}
          className={`w-28 rounded-lg border bg-white px-2 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none ${
            error ? 'border-red-500' : 'border-gray-300 focus:border-primary-500'
          }`}
        />
        <span className="text-xs text-gray-500">per brand</span>
      </div>
      {error ? (
        <span role="alert" className="text-xs text-red-600 sm:text-right">{error}</span>
      ) : (
        <span className="text-xs text-gray-500 sm:text-right">Up to 10. Empty = system maximum (10).</span>
      )}
    </div>
  );
};

export const PackageEntitlementsSection = ({
  packageId,
  isAgency,
  maxBrandProfiles,
  onCommitMaxBrandProfiles,
  onMaxBrandProfilesInvalidChange,
  draft,
}: Props) => {
  const isDraft = packageId === null;
  const { data: savedData, isLoading: savedLoading, error: savedError, refetch: refetchSaved } =
    usePackageEntitlements(packageId ?? 0);
  const registry = useFeatureRegistry(isDraft);
  const setEntitlement = useSetPackageEntitlement(packageId ?? 0);
  const { data: savedQuotaRows } = usePackageQuotas(packageId ?? 0);
  const { data: quotaDefs } = useQuotaRegistry(isDraft);
  const setQuota = useSetPackageQuota(packageId ?? 0);
  const [quotaResetKey, setQuotaResetKey] = useState(0);

  const isLoading = isDraft ? registry.isLoading : savedLoading;
  const error = isDraft ? registry.error : savedError;
  const refetch = isDraft ? registry.refetch : refetchSaved;

  // Draft: row-source registry keys only (column features live in the package fields above), default ON
  const data = useMemo(() => {
    if (!isDraft) return savedData;
    const features: PackageEntitlementToggle[] = (registry.data?.features ?? [])
      .filter((f) => f.source === 'row')
      .map((f) => ({ ...f, enabled: draft?.toggles[f.key] ?? true, hasRow: true }));
    return { packageId: 0, features };
  }, [isDraft, savedData, registry.data, draft?.toggles]);

  const quotaRows = useMemo<PackageQuotaRow[] | undefined>(() => {
    if (!isDraft) return savedQuotaRows;
    return quotaDefs?.filter((q) => q.enabled).map((q) => ({ ...q, monthlyLimit: draft?.quotas[q.quotaKey]?.monthlyLimit ?? null }));
  }, [isDraft, savedQuotaRows, quotaDefs, draft?.quotas]);

  // Display only: features of portfolio-only groups are hidden for non-portfolio packages (no rows are touched)
  const hiddenFeatureKeys = useMemo(() => {
    const keys = new Set<string>();
    if (isAgency) return keys;
    for (const f of data?.features ?? []) {
      if (PORTFOLIO_ONLY_GROUPS.has(f.group)) keys.add(f.key);
    }
    return keys;
  }, [data, isAgency]);

  // Quota rows that are switched on in the registry; linked ones render under their feature toggle
  const enabledQuotas = useMemo(
    () => (quotaRows ?? []).filter((q) => q.enabled && !(q.featureKey && hiddenFeatureKeys.has(q.featureKey))),
    [quotaRows, hiddenFeatureKeys]
  );
  const quotasByFeature = useMemo(() => {
    const map = new Map<string, PackageQuotaRow[]>();
    for (const q of enabledQuotas) {
      if (!q.featureKey) continue;
      map.set(q.featureKey, [...(map.get(q.featureKey) ?? []), q]);
    }
    return map;
  }, [enabledQuotas]);
  const standaloneQuotas = useMemo(
    () => enabledQuotas.filter((q) => !q.featureKey || !(data?.features ?? []).some((f) => f.key === q.featureKey && !f.notEnforced)),
    [enabledQuotas, data]
  );

  const handleQuotaCommit = (quota: PackageQuotaRow, monthlyLimit: number | null) => {
    if (draft && isDraft) {
      draft.onQuotaChange(quota, monthlyLimit);
      return;
    }
    setQuota.mutate(
      { quotaKey: quota.quotaKey, monthlyLimit },
      {
        onSuccess: () =>
          toast.success(
            `${quota.label}: ${monthlyLimit === null ? 'Unlimited' : `${monthlyLimit} ${quota.unitLabel} per month`}`
          ),
        onError: (err: unknown) => {
          setQuotaResetKey((k) => k + 1);
          toast.error(formatApiError(err).message || `Could not update ${quota.label}`);
        },
      }
    );
  };

  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: PackageEntitlementToggle[] }>();
    for (const f of data?.features ?? []) {
      if (f.notEnforced) continue; // ungated keys are not offered in the admin
      if (hiddenFeatureKeys.has(f.key)) continue;
      const g = map.get(f.group) ?? { label: f.groupLabel, items: [] };
      g.items.push(f);
      map.set(f.group, g);
    }
    return Array.from(map.entries());
  }, [data, hiddenFeatureKeys]);

  const handleToggle = (feature: PackageEntitlementToggle, enabled: boolean) => {
    if (feature.notEnforced) return;
    if (draft && isDraft) {
      draft.onToggle(feature.key, enabled);
      return;
    }
    setEntitlement.mutate(
      { key: feature.key, enabled },
      {
        onSuccess: () => toast.success(`${feature.label}: ${enabled ? 'on' : 'off'}`),
        onError: (err: unknown) =>
          toast.error(formatApiError(err).message || `Could not update ${feature.label}`),
      }
    );
  };

  return (
    <div className="space-y-4" data-testid="package-entitlements">
      <div>
        <h3 className="font-semibold text-gray-900">Feature access</h3>
        <p className="text-xs text-gray-500 mt-1">
          {isDraft
            ? 'These settings are saved when you create the package. Canva, Brand Check, Google Drive, Analytics and Atelier Mode are set in the package fields above.'
            : 'Changes here save immediately and do not need the Update button.'}
        </p>
      </div>

      {isLoading && <LoadingSpinner />}

      {error && (
        <div role="alert" className="text-sm text-red-600">
          Could not load feature toggles: {formatApiError(error).message}{' '}
          <button type="button" onClick={() => refetch()} className="underline font-medium">
            Try again
          </button>
        </div>
      )}

      {standaloneQuotas.length > 0 && (
        <section aria-label="Monthly limits" className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Monthly limits</h4>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {standaloneQuotas.map((q) => (
              <li key={q.quotaKey} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3">
                <span className="text-sm font-medium text-gray-900">{q.label}</span>
                <QuotaInput quota={q} disabled={setQuota.isPending} resetKey={quotaResetKey} onCommit={handleQuotaCommit} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {groups.map(([group, { label, items }]) => (
        <section key={group} aria-label={label} className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</h4>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {items.map((f) => {
              const badge = SOURCE_BADGE[f.source];
              const notSet = f.source === 'row' && !f.hasRow;
              return (
                <li
                  key={f.key}
                  className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 p-3 ${notSet ? 'bg-amber-50' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-sm font-medium text-gray-900">{f.label}</span>
                      <span
                        title={badge.title}
                        className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{f.description}</p>
                    {f.uiOnly && (
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1" data-testid="ui-only-note">
                        <i className={`hm hm-${ICONS.INFO}`} aria-hidden="true" />
                        Hidden in the app only (server not locked yet).
                      </p>
                    )}
                    {f.source === 'addon' && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Teams can also get this through an add-on, independent of this switch.
                      </p>
                    )}
                    {notSet && (
                      <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                        <i className={`hm hm-${ICONS.WARNING}`} aria-hidden="true" />
                        Off (not set): no row yet, so it is locked once gating is live.
                      </p>
                    )}
                  </div>
                  {f.key === 'brand_world' && (
                    <BrandProfilesInput
                      saved={maxBrandProfiles}
                      onCommit={onCommitMaxBrandProfiles}
                      onInvalidChange={onMaxBrandProfilesInvalidChange}
                    />
                  )}
                  {(quotasByFeature.get(f.key) ?? []).map((q) => (
                    <QuotaInput key={q.quotaKey} quota={q} disabled={setQuota.isPending} resetKey={quotaResetKey} onCommit={handleQuotaCommit} />
                  ))}
                  <div className="sm:shrink-0 flex items-center justify-between sm:justify-start gap-2">
                    <span className="text-xs text-gray-500 sm:w-20 sm:text-right">
                      {notSet ? 'Off (not set)' : f.enabled ? 'On' : 'Off'}
                    </span>
                    <Toggle
                      ariaLabel={f.label}
                      checked={f.enabled}
                      disabled={!isDraft && setEntitlement.isPending}
                      onChange={(checked) => handleToggle(f, checked)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default PackageEntitlementsSection;
